// src/features/equipment/pages/EquipmentDetailsPage/components/EquipmentDetailsCard.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Stack,
  Alert,
} from '@mui/material';
import {
  Devices as DevicesIcon,
  Business as BusinessIcon,
  ModelTraining as ModelTrainingIcon,
  Tag as TagIcon,
  Bolt as BoltIcon,
  CalendarMonth as CalendarMonthIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import LocalizedDateDistance from '../../../../../i18n/LocalizedDateDistance';
import SectionHeader from '../../../../../components/common/SectionHeader';
import { RADIOLOGY_TYPES } from '../../../utils/enums';

export default function EquipmentDetailsCard({ equipment }) {
  const { t } = useTranslation('equipment');

  const isRadiologyEquipment = RADIOLOGY_TYPES.includes(equipment.equipment_type);

  return (
    <Box sx={{ flex: 1 }}>
      <SectionHeader icon={DevicesIcon} title={t('equipment:form.equipment_information')} />

      {isRadiologyEquipment && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('equipment:form.radiology_info')}
          </Alert>
        )}

        <Stack spacing={2}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('equipment:labels.equipment_type')}
            </Typography>
            <Typography variant="body1" fontWeight="medium">
              {t(`equipment:equipment_types.${equipment.equipment_type}`)}
            </Typography>
          </Box>

          {equipment.manufacturer && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={0.5}>
                <BusinessIcon fontSize="small" />
                {t('equipment:labels.manufacturer')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {equipment.manufacturer}
              </Typography>
            </Box>
          )}

          {equipment.model && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={0.5}>
                <ModelTrainingIcon fontSize="small" />
                {t('equipment:labels.model')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {equipment.model}
              </Typography>
            </Box>
          )}

          {equipment.serial_number && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={0.5}>
                <TagIcon fontSize="small" />
                {t('equipment:labels.serial_number')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {equipment.serial_number}
              </Typography>
            </Box>
          )}

          {equipment.batch_number && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={0.5}>
                <TagIcon fontSize="small" />
                {t('equipment:labels.batch_number')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {equipment.batch_number}
              </Typography>
            </Box>
          )}

          {equipment.max_voltage_kv && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={0.5}>
                <BoltIcon fontSize="small" />
                {t('equipment:labels.max_voltage_kv')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {/* eslint-disable-next-line i18next/no-literal-string */}
                {equipment.max_voltage_kv} kV
              </Typography>
            </Box>
          )}

          {equipment.last_receipt_control_date && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={0.5}>
                <CalendarMonthIcon fontSize="small" />
                {t('equipment:labels.last_receipt_control_date')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {new Date(equipment.last_receipt_control_date).toLocaleDateString()}
                {' '}
                (<LocalizedDateDistance date={equipment.last_receipt_control_date} />)
              </Typography>
            </Box>
          )}

          {equipment.notes && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={0.5}>
                <DescriptionIcon fontSize="small" />
                {t('equipment:labels.notes')}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {equipment.notes}
              </Typography>
            </Box>
          )}
      </Stack>
    </Box>
  );
}